import {
  CategoryData,
  useGetBookkeepingSettingsQuery,
  useUpdateBookkeepingSettingsMutation,
} from "store/apis/booksLockDate";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { useState } from "react";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { DefaultCategoriesAccordion } from "./DefaultCategoriesAccordion";
import { CategorySelector } from "components/CategorySelector/CategorySelector";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { TxnCategories } from "types/Models/books";
import { getSectionCategoryPrefixes } from "utils/getSectionCategoryPrefixes";
import { useWindowSize } from "hooks/useWindowSize";

type DefaultCategoryChange = {
  selectedCategoryId: string;
  selectedCategoryName: string;
  categoryData: CategoryData;
};

type SettingsCategorySelectorProps = {
  row: CategoryData;
  isDisabled: boolean;
  onCategoryChange: (change: DefaultCategoryChange) => void;
};

type CategoryUpdateData = {
  selectedCategoryId: string;
  selectedCategoryName: string;
  sectionCategoryName: string;
};

const EMPTY_CATEGORY_DATA: CategoryData[] = [];
const EMPTY_CHART_OF_ACCOUNTS: TxnCategories[] = [];
const DESKTOP_MENU_PORTAL_STYLE = {
  width: 450,
  marginLeft: -230,
};

const SettingsCategorySelector = ({
  row,
  isDisabled,
  onCategoryChange,
}: SettingsCategorySelectorProps) => {
  const { chartOfAccounts = EMPTY_CHART_OF_ACCOUNTS } = useChartOfAccounts({});
  const { isMobile } = useWindowSize();
  const prefixes = getSectionCategoryPrefixes(row.section_category_identifiers);

  const options =
    prefixes.length === 0
      ? chartOfAccounts
      : chartOfAccounts.filter((category) =>
          prefixes.includes(category.identifier_prefix)
        );

  const handleChange = (value: { value: string; name: string } | null) => {
    if (!value) return;

    onCategoryChange({
      selectedCategoryId: value.value,
      selectedCategoryName: value.name,
      categoryData: row,
    });
  };

  return (
    <CategorySelector
      name={row.uuid}
      category={row.category?.uuid ?? ""}
      hiddenCategoryTypes={[]}
      withForm={false}
      isClearable={false}
      hideAction={true}
      menuPortalStyle={isMobile ? undefined : DESKTOP_MENU_PORTAL_STYLE}
      menuPlacement={isMobile ? "auto" : undefined}
      customCategoryOptions={options}
      isDisabled={isDisabled}
      onChange={handleChange}
    />
  );
};

export const DefaultCategories = ({ entityId }: { entityId: string }) => {
  usePageTitle("Default Category");
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.ACCOUNTING
  );
  const {
    data: settingsData,
    isLoading,
    isSuccess,
  } = useGetBookkeepingSettingsQuery({ entityId });

  const [updateBookkeepingSettings, { isLoading: isUpdating }] =
    useUpdateBookkeepingSettingsMutation();

  const { successToast, alertToast } = useToast();

  const confirmModal = useModal();
  const warningModal = useModal();
  const [categoryUpdate, setCategoryUpdate] =
    useState<CategoryUpdateData | null>(null);
  const [duplicateCategoryInfo, setDuplicateCategoryInfo] = useState<{
    selectedCategoryName: string;
    existingCategoryRow: string;
  } | null>(null);

  const handleCategoryChange = ({
    selectedCategoryId,
    selectedCategoryName,
    categoryData,
  }: DefaultCategoryChange) => {
    const existingSelectedCategory = settingsData?.category_data?.find(
      (row) => row.category?.uuid === selectedCategoryId
    );

    if (existingSelectedCategory) {
      setDuplicateCategoryInfo({
        selectedCategoryName: selectedCategoryName,
        existingCategoryRow: existingSelectedCategory.title,
      });
      warningModal.open();
      return;
    }

    setCategoryUpdate({
      selectedCategoryId,
      selectedCategoryName,
      sectionCategoryName: categoryData.category_name,
    });
    confirmModal.open();
  };

  const handleUpdateCategory = async () => {
    if (!categoryUpdate) return;

    try {
      const res = await updateBookkeepingSettings({
        entityId,
        transaction_category_id: categoryUpdate.selectedCategoryId,
        category_name: categoryUpdate.sectionCategoryName,
      }).unwrap();

      successToast({
        title: `Category updated to ${categoryUpdate.selectedCategoryName} successfully`,
      });
      confirmModal.close();
      setCategoryUpdate(null);
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  const renderSelector = (row: CategoryData) => (
    <SettingsCategorySelector
      row={row}
      isDisabled={!hasWritePermission}
      onCategoryChange={handleCategoryChange}
    />
  );

  const handleCloseModal = () => {
    confirmModal.close();
    setCategoryUpdate(null);
  };

  return (
    <Async.Root isLoading={isLoading} isSuccess={isSuccess} isEmpty={false}>
      <Async.Success>
        <div className="t-pb-20">
          <DefaultCategoriesAccordion
            categoryData={settingsData?.category_data ?? EMPTY_CATEGORY_DATA}
            renderSelector={renderSelector}
          />
        </div>

        <Modal.Root open={confirmModal.isOpen} onOpenChange={handleCloseModal}>
          <Modal.Content size="small">
            <StatusModal type="warning" title="Are you sure?">
              <p>
                This will update the default category to{" "}
                <strong>{categoryUpdate?.selectedCategoryName}</strong> for all
                future entries. Existing entries will remain unchanged.
              </p>
              <div className="t-flex t-gap-2 t-justify-end t-mt-6">
                <Button customType="secondary" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button
                  customType="primary"
                  onClick={handleUpdateCategory}
                  isLoading={isUpdating}
                >
                  Update
                </Button>
              </div>
            </StatusModal>
          </Modal.Content>
        </Modal.Root>

        <Modal.Root
          open={warningModal.isOpen}
          onOpenChange={warningModal.close}
        >
          <Modal.Content size="small">
            <StatusModal type="warning" title="Category Already in Use">
              <p>
                <strong>{duplicateCategoryInfo?.selectedCategoryName}</strong>{" "}
                is currently set as the default category for{" "}
                <strong>{duplicateCategoryInfo?.existingCategoryRow}</strong>.
                Update the category for{" "}
                <strong>{duplicateCategoryInfo?.existingCategoryRow}</strong> to
                able to assign it here.
              </p>
              <div className="t-flex t-gap-2 t-justify-end t-mt-6">
                <Button customType="primary" onClick={warningModal.close}>
                  Okay
                </Button>
              </div>
            </StatusModal>
          </Modal.Content>
        </Modal.Root>
      </Async.Success>
    </Async.Root>
  );
};
